import React, { FC } from 'react';
import {
  Avatar,
  Badge,
  Card,
  ColumnLayout,
  CopyButton,
  Empty,
  FlexGroup,
  FlexItem,
  KeyValueList,
  Loading,
  Tag,
  Text,
  ViewLayout,
} from '@lithos';
import { CalloutProps } from '@lithos/Callout';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useGetIngestServerGroup } from '../../../api/serverGroup';
import { Status, IngestServerGroup } from '../../../types/tecton_proto/servergroupservice/server_group_service';
import ServerGroupStatusBadge from './ServerGroupStatusBadge';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';
import IngestServerGroupSourcesTableView from './IngestServerGroupsSourcesTableView';
import IngestServerGroupMonitoring from './IngestServerGroupMonitoring';

const IngestGroupMetadataView: FC<{ serverGroup: IngestServerGroup }> = ({ serverGroup }) => {
  const metadataItems = [
    {
      title: 'Name',
      content: serverGroup.name,
    },
    {
      title: 'ID',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row" alignItems="center">
          <FlexItem grow={0}>
            <Text code>{serverGroup.id}</Text>
          </FlexItem>
          <FlexItem grow={0}>
            <CopyButton code={serverGroup.id ?? ''} />
          </FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Status',
      content: <ServerGroupStatusBadge status={serverGroup.status ?? Status.UNSPECIFIED} />,
    },
    {
      title: 'Type',
      content: 'Ingest Server Group',
    },
    {
      title: 'Owner',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row">
          {!serverGroup.metadata?.owner && <Text>-</Text>}
          {serverGroup.metadata?.owner && (
            <>
              <FlexItem grow={0}>
                <Avatar name={serverGroup.metadata?.owner} />
              </FlexItem>
              <FlexItem grow={0}>
                <Text>{serverGroup.metadata?.owner}</Text>
              </FlexItem>
            </>
          )}
        </FlexGroup>
      ),
    },
    {
      title: 'Created At',
      content: serverGroup.created_at ? dateTimeFormat(new Date(serverGroup.created_at), timeZone.UTC) : '-',
    },
    {
      title: 'Last Updated At',
      content: serverGroup.updated_at ? dateTimeFormat(new Date(serverGroup.updated_at), timeZone.UTC) : '-',
    },
    {
      title: 'Tags',
      content: serverGroup.metadata?.tags
        ? Object.entries(serverGroup.metadata.tags).map(([key, value]) => {
            return <Tag category={key} label={value} size="s" icon />;
          })
        : [],
    },
  ];

  const configItems = [
    {
      title: 'Node Type',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row">
          <FlexItem grow="0" shrink="0">
            {serverGroup.node_type}
          </FlexItem>
          <FlexItem grow="0" shrink="0">
            {serverGroup.pending_config?.node_type && (
              <Badge label={`Pending: ${serverGroup.pending_config?.node_type}`} color="gray" size="s" icon="Refresh" />
            )}
          </FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Scaling Mode',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row">
          <FlexItem grow="0" shrink="0">
            {serverGroup.autoscaling_config ? 'Autoscaling' : 'Provisioned'}
          </FlexItem>
          <FlexItem grow="0" shrink="0">
            {serverGroup.pending_config?.autoscaling_config && (
              <Badge label="Pending: Autoscaling" color="gray" size="s" icon="Refresh" />
            )}
            {serverGroup.pending_config?.provisioned_config && (
              <Badge label="Pending: Provisioned" color="gray" size="s" icon="Refresh" />
            )}
          </FlexItem>
        </FlexGroup>
      ),
    },
    ...(serverGroup.provisioned_config
      ? [
          {
            title: 'Desired Nodes',
            content: (
              <FlexGroup padding="0" gapSize="2" direction="row">
                {!serverGroup.provisioned_config?.desired_nodes && <Text>-</Text>}
                {serverGroup.provisioned_config?.desired_nodes && (
                  <FlexItem grow="0" shrink="0">
                    {serverGroup.provisioned_config?.desired_nodes}
                  </FlexItem>
                )}
                <FlexItem grow="0" shrink="0">
                  {serverGroup.pending_config?.provisioned_config?.desired_nodes && (
                    <Badge
                      label={`Pending: ${serverGroup.pending_config.provisioned_config.desired_nodes}`}
                      color="gray"
                      size="s"
                      icon="Refresh"
                    />
                  )}
                </FlexItem>
              </FlexGroup>
            ),
          },
        ]
      : []),
    ...(serverGroup.autoscaling_config
      ? [
          {
            title: 'Min Nodes',
            content: (
              <FlexGroup padding="0" gapSize="2" direction="row">
                <FlexItem grow="0" shrink="0">
                  {serverGroup.autoscaling_config?.min_nodes ?? '-'}
                </FlexItem>
                <FlexItem grow="0" shrink="0">
                  {serverGroup.pending_config?.autoscaling_config?.min_nodes && (
                    <Badge label={serverGroup.autoscaling_config?.min_nodes} color="gray" size="s" icon="Refresh" />
                  )}
                </FlexItem>
              </FlexGroup>
            ),
          },
          {
            title: 'Max Nodes',
            content: (
              <FlexGroup padding="0" gapSize="2" direction="row">
                <FlexItem grow="0" shrink="0">
                  {serverGroup.autoscaling_config?.max_nodes ?? '-'}
                </FlexItem>
                <FlexItem grow="0" shrink="0">
                  {serverGroup.pending_config?.autoscaling_config?.max_nodes && (
                    <Badge label={serverGroup.autoscaling_config?.max_nodes} color="gray" size="s" icon="Refresh" />
                  )}
                </FlexItem>
              </FlexGroup>
            ),
          },
        ]
      : []),
  ];

  return (
    <ColumnLayout>
      <Card title="Metadata">
        <KeyValueList items={metadataItems} />
      </Card>
      <Card title="Configuration">
        <KeyValueList items={configItems} />
      </Card>
    </ColumnLayout>
  );
};

const IngestServerGroupContainer: FC = () => {
  const navigate = useNavigate();
  const { workspace } = useParams();
  const { id } = useParams();
  const { data, isLoading, isError } = useGetIngestServerGroup(workspace ?? '', id ?? '');
  const pathSegments = location.pathname.split('/');
  const currentPath = pathSegments[pathSegments.length - 1];
  const selectedTab =
    currentPath === 'metadata' ? 'Metadata' : currentPath === 'data-sources' ? 'Data Sources' : 'Monitoring';

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Empty
        placement="full-page"
        actions={[]}
        useCase="errorUnknown"
        title="Unknown Error"
        description="Error loading Ingest Server Group"
      />
    );
  }

  const calloutProps: Record<Status, CalloutProps | undefined> = {
    [Status.UNSPECIFIED]: undefined,
    [Status.ERROR]: undefined,
    [Status.PENDING]: {
      title: 'Updating Configuration',
      content: 'Changes typically take effect within 1 minute.',
      singleLine: true,
    },
    [Status.READY]: undefined,
    [Status.CREATING]: {
      title: 'Ingest Server Group',
      content: 'A Transform Server Group is a collection of feature views that are used to transform data.',
      singleLine: true,
    },
    [Status.UPDATING]: {
      title: 'Updating Configuration',
      content: 'Changes typically take effect within 1 minute.',
      singleLine: true,
    },
    [Status.DELETING]: {
      title: 'Deletion in Progress',
      content: 'This server group is scheduled for deletion and will be removed from the database within minutes.',
      singleLine: true,
      variant: 'warning',
    },
  };

  const ingestServerGroupRoutes = [
    {
      label: 'Metadata',
      route: 'metadata',
    },
    {
      label: 'Monitoring',
      route: 'monitoring',
    },
    {
      label: 'Data Sources',
      route: 'data-sources',
    },
  ];

  return (
    <ViewLayout
      header={{
        breadcrumbs: {
          root: {
            label: workspace ?? '',
            url: `/repo/${workspace}`,
          },
          items: [
            {
              label: 'Server Groups',
              url: `/repo/${workspace}/server-groups`,
            },
            {
              label: 'Ingest Server Groups',
              url: `/repo/${workspace}/server-groups/ingest-server-groups`,
            },
            {
              label: data?.name,
            },
          ],
        },
        callout: calloutProps[data.status ?? Status.UNSPECIFIED],
        tabs: {
          items: ingestServerGroupRoutes,
          selectedTab: selectedTab,
          setSelectedTab: (tab: string) => {
            const route = ingestServerGroupRoutes.find((route) => route.label === tab)?.route;
            if (route) {
              navigate(route);
            }
          },
        },
        title: data?.name ?? '',
      }}
      content={
        <Routes>
          <Route index element={<Navigate to={'metadata'} />} />
          <Route path={'metadata'} element={<IngestGroupMetadataView serverGroup={data} />} />
          <Route path={'data-sources'} element={<IngestServerGroupSourcesTableView serverGroup={data} />} />
          <Route path={'monitoring'} element={<IngestServerGroupMonitoring />} />
        </Routes>
      }
    />
  );
};

export default IngestServerGroupContainer;
